import { useCallback, useEffect, useId, useState } from 'react';

/**
 * Real refractive "liquid glass" material — an SVG displacement filter
 * (Snell's-law bend along a rounded-rect bezel, plus a rim-light map),
 * generated from the element's own measured size and applied via
 * `backdrop-filter: url(#filter) blur() saturate()`. Chromium/WebKit only;
 * see `ok` check below.
 *
 * Ported from a standalone tuning prototype — the same generation code, with
 * a fixed settings object instead of the prototype's live control panel.
 */
export interface LiquidGlassSettings {
  /** Corner radius, px — clamped to the element's own half-width/height. */
  radius: number;
  /** Bend curve along the bezel. */
  profile: 'linear' | 'convex' | 'concave' | 'lip';
  /** Width of the refracting rim, px. */
  bezel: number;
  /** Simulated glass thickness — taller bezel bend at higher values. */
  thickness: number;
  /** Index of refraction (glass ≈ 1.45–1.6). */
  ior: number;
  /** Displacement scale multiplier on top of the physical simulation. */
  refraction: number;
  /** `backdrop-filter` blur, px. */
  blur: number;
  /** `backdrop-filter` saturate, %. */
  saturate: number;
  /** Tint color layered under the refracted backdrop. */
  tint: string;
  /** Tint opacity, 0–1. */
  tintOpacity: number;
  /** Rim-light strength, 0–1 (0 disables the specular map entirely). */
  highlight: number;
  /** Rim-light falloff sharpness. */
  highlightFocus: number;
  /** Rim-light direction, degrees. */
  lightAngle: number;
  /** Light both the lobe facing `lightAngle` and the one opposite it. */
  bipolar: boolean;
  /** Edge-line opacity (conic-gradient ring), 0–1. */
  edge: number;
  /** Edge-line opacity at the two lit corners, 0–1. */
  edgePeak: number;
  /** Outer drop shadow strength, 0–1 (0 disables it). */
  shadow: number;
}

const SVGNS = 'http://www.w3.org/2000/svg';
const XLINKNS = 'http://www.w3.org/1999/xlink';
const DEFS_ID = 'liquid-glass-defs';

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const PROFILES: Record<LiquidGlassSettings['profile'], (t: number) => number> = {
  linear: (t) => t,
  convex: (t) => Math.sin((t * Math.PI) / 2),
  concave: (t) => 1 - Math.cos((t * Math.PI) / 2),
  lip: (t) => Math.sin(Math.min(t, 1) * Math.PI),
};

/** Signed distance to a rounded rect centred at 0,0. */
function sdf(px: number, py: number, hw: number, hh: number, r: number) {
  const qx = Math.abs(px) - (hw - r);
  const qy = Math.abs(py) - (hh - r);
  return Math.min(Math.max(qx, qy), 0) + Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - r;
}

/** Snell's law along the bezel → lateral displacement per sample. */
function radiusProfile(
  name: LiquidGlassSettings['profile'],
  bezel: number,
  thickness: number,
  ior: number,
  samples = 128,
) {
  const f = PROFILES[name] || PROFILES.convex;
  const disp = new Float32Array(samples);
  const d = 0.001;
  for (let i = 0; i < samples; i++) {
    const t = i / (samples - 1);
    const t0 = clamp(t - d, 0, 1);
    const t1 = clamp(t + d, 0, 1);
    const dt = t1 - t0 || d;
    const slope = (f(t1) - f(t0)) / dt;
    const theta1 = Math.atan(slope * (thickness / Math.max(bezel, 1e-4)));
    const theta2 = Math.asin(clamp(Math.sin(theta1) / Math.max(ior, 1e-3), -1, 1));
    disp[i] = thickness * (Math.tan(theta1) - Math.tan(theta2));
  }
  let maxAbs = 1e-6;
  for (const v of disp) maxAbs = Math.max(maxAbs, Math.abs(v));
  const norm = new Float32Array(samples);
  for (let i = 0; i < samples; i++) norm[i] = disp[i] / maxAbs;
  return { norm, maxAbs };
}

function sampleProfile(norm: Float32Array, t: number) {
  t = clamp(t, 0, 1);
  const pos = t * (norm.length - 1);
  const i0 = Math.floor(pos);
  const i1 = Math.min(norm.length - 1, i0 + 1);
  return lerp(norm[i0], norm[i1], pos - i0);
}

/** R = X shift, G = Y shift. */
function displacementMap(
  w: number,
  h: number,
  radius: number,
  bezel: number,
  profileData: { norm: Float32Array; maxAbs: number },
) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const data = img.data;
  const hw = w / 2;
  const hh = h / 2;
  const r = Math.min(radius, hw, hh);
  const e = 0.75;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const px = x - hw + 0.5;
      const py = y - hh + 0.5;
      const dist = sdf(px, py, hw, hh, r);
      const i = (y * w + x) * 4;
      if (dist > 0.5) {
        data[i] = data[i + 1] = data[i + 2] = 128;
        data[i + 3] = 0;
        continue;
      }
      const fromEdge = -dist;
      if (fromEdge >= bezel) {
        data[i] = data[i + 1] = data[i + 2] = 128;
        data[i + 3] = 255;
        continue;
      }
      const t = clamp(fromEdge / Math.max(bezel, 1e-4), 0, 1);
      const mag = sampleProfile(profileData.norm, t);
      const dxs = sdf(px + e, py, hw, hh, r) - sdf(px - e, py, hw, hh, r);
      const dys = sdf(px, py + e, hw, hh, r) - sdf(px, py - e, hw, hh, r);
      const len = Math.hypot(dxs, dys) || 1;
      data[i] = clamp(Math.round(128 + clamp((dxs / len) * mag, -1, 1) * 127), 0, 255);
      data[i + 1] = clamp(Math.round(128 + clamp((dys / len) * mag, -1, 1) * 127), 0, 255);
      data[i + 2] = 128;
      data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

/** Rim light — with `highlight` at 0 this renders fully transparent. */
function specularMap(
  w: number,
  h: number,
  radius: number,
  bezel: number,
  angleRad: number,
  sharpness: number,
  opacity: number,
  bipolar: boolean,
) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(w, h);
  const data = img.data;
  const hw = w / 2;
  const hh = h / 2;
  const r = Math.min(radius, hw, hh);
  const e = 0.75;
  const lx = Math.cos(angleRad);
  const ly = Math.sin(angleRad);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const px = x - hw + 0.5;
      const py = y - hh + 0.5;
      const dist = sdf(px, py, hw, hh, r);
      data[i] = data[i + 1] = data[i + 2] = 255;
      if (dist > 0.5 || -dist >= bezel) {
        data[i + 3] = 0;
        continue;
      }
      const t = clamp(-dist / Math.max(bezel, 1e-4), 0, 1);
      const dxs = sdf(px + e, py, hw, hh, r) - sdf(px - e, py, hw, hh, r);
      const dys = sdf(px, py + e, hw, hh, r) - sdf(px, py - e, hw, hh, r);
      const len = Math.hypot(dxs, dys) || 1;
      const raw = (dxs / len) * lx + (dys / len) * ly;
      // Bipolar: light both the lobe facing the light and the one opposite it.
      const dot = bipolar ? Math.abs(raw) : clamp(raw, 0, 1);
      data[i + 3] = clamp(Math.round(Math.pow(dot, sharpness) * Math.pow(1 - t, 1.15) * opacity * 255), 0, 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

const setHref = (el: SVGElement, url: string) => {
  el.setAttributeNS(XLINKNS, 'xlink:href', url);
  el.setAttribute('href', url);
};

const hexToRgb = (hex: string) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 255, g: 255, b: 255 };
};

// How far cornerConicAngle lets an element's aspect ratio push the highlight
// toward the horizontal/vertical axis before clamping. On a true capsule
// (e.g. the ~18.5:1 header pill) the *unclamped* corner angle collapses
// almost onto the side edges — geometrically correct, but it reads as
// "lit at both ends" instead of the diagonal top-left/bottom-right sheen
// the near-square UserMenu/cards show. Capping the ratio fed into atan2
// keeps every surface's highlight reading as roughly the same diagonal,
// regardless of how extreme its actual proportions are.
const MAX_CORNER_ASPECT_RATIO = 2.5;

/**
 * Angle (CSS conic-gradient convention: 0deg = up, clockwise) from an
 * element's center to its top-left corner, given its actual width/height —
 * used to aim the edge ring's highlight lobe (see AuthLayout.module.scss's
 * own conic-gradient ::before, which reads --edge-lo/--edge-hi/
 * --edge-peak-angle from this hook) at the real corner instead of a
 * hardcoded 135deg/315deg that only lands there for a roughly-square
 * element. The aspect ratio is clamped (see MAX_CORNER_ASPECT_RATIO above)
 * before the angle is computed, so a very wide/short (or tall/narrow)
 * element still gets a diagonal-reading highlight instead of one that
 * collapses onto an edge.
 */
function cornerConicAngle(w: number, h: number): number {
  const clampedW = Math.min(w, h * MAX_CORNER_ASPECT_RATIO);
  const clampedH = Math.min(h, w * MAX_CORNER_ASPECT_RATIO);
  const standardAngle = (Math.atan2(clampedH / 2, -(clampedW / 2)) * 180) / Math.PI;
  const deg = 90 - standardAngle;
  return ((deg % 360) + 360) % 360;
}

/** Lazily creates the single hidden `<svg><defs>` every filter is appended to. */
function getDefs(): SVGDefsElement {
  let defs = document.getElementById(DEFS_ID) as SVGDefsElement | null;
  if (defs) return defs;
  const svg = document.createElementNS(SVGNS, 'svg');
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.setAttribute('aria-hidden', 'true');
  defs = document.createElementNS(SVGNS, 'defs') as SVGDefsElement;
  defs.id = DEFS_ID;
  svg.appendChild(defs);
  document.body.appendChild(svg);
  return defs;
}

interface FilterRefs {
  filter: SVGFilterElement;
  disp: SVGFEImageElement;
  dm: SVGFEDisplacementMapElement;
  spec: SVGFEImageElement;
}

function makeFilter(id: string): FilterRefs {
  const filter = document.createElementNS(SVGNS, 'filter') as SVGFilterElement;
  filter.setAttribute('id', id);
  filter.setAttribute('x', '-25%');
  filter.setAttribute('y', '-25%');
  filter.setAttribute('width', '150%');
  filter.setAttribute('height', '150%');
  filter.setAttribute('color-interpolation-filters', 'sRGB');
  const disp = document.createElementNS(SVGNS, 'feImage') as SVGFEImageElement;
  disp.setAttribute('result', 'dispMap');
  disp.setAttribute('x', '0');
  disp.setAttribute('y', '0');
  const dm = document.createElementNS(SVGNS, 'feDisplacementMap') as SVGFEDisplacementMapElement;
  dm.setAttribute('in', 'SourceGraphic');
  dm.setAttribute('in2', 'dispMap');
  dm.setAttribute('xChannelSelector', 'R');
  dm.setAttribute('yChannelSelector', 'G');
  dm.setAttribute('result', 'refracted');
  const spec = document.createElementNS(SVGNS, 'feImage') as SVGFEImageElement;
  spec.setAttribute('result', 'specMap');
  spec.setAttribute('x', '0');
  spec.setAttribute('y', '0');
  const blend = document.createElementNS(SVGNS, 'feBlend');
  blend.setAttribute('in', 'refracted');
  blend.setAttribute('in2', 'specMap');
  blend.setAttribute('mode', 'screen');
  filter.append(disp, dm, spec, blend);
  getDefs().appendChild(filter);
  return { filter, disp, dm, spec };
}

// Same physical settings (bezel/thickness/ior/refraction) produce a bigger
// feDisplacementMap scale on a short element than a tall/square one, since
// `bezel` clamps to at most half the *smaller* dimension: a short bar with
// the same `thickness` bends over a narrower band, i.e. more steeply, which
// is a larger maxAbs. Left alone, a wide capsule (e.g. the header pill) ends
// up with visibly more refraction than a near-square panel (e.g. the avatar
// menu) using the identical settings object. Capping the final scale keeps
// every surface's refraction reading as roughly the same strength — tuned
// to sit at the near-square case's own natural value so *that* shape is
// unaffected and only flatter/taller outliers get pulled down to match.
const MAX_DISPLACEMENT_SCALE = 75;

/** Measures `el`, regenerates its maps, and applies the material via inline styles. */
function applyGlass(el: HTMLElement, refs: FilterRefs, s: LiquidGlassSettings) {
  const rect = el.getBoundingClientRect();
  const w = Math.max(2, Math.round(rect.width));
  const h = Math.max(2, Math.round(rect.height));
  const radius = Math.min(s.radius, w / 2, h / 2);
  const bezel = Math.min(s.bezel, Math.min(w, h) / 2);
  const pd = radiusProfile(s.profile, bezel, s.thickness, s.ior);

  refs.disp.setAttribute('width', String(w));
  refs.disp.setAttribute('height', String(h));
  setHref(refs.disp, displacementMap(w, h, radius, bezel, pd));
  refs.dm.setAttribute('scale', Math.min(pd.maxAbs * s.refraction, MAX_DISPLACEMENT_SCALE).toFixed(2));
  refs.spec.setAttribute('width', String(w));
  refs.spec.setAttribute('height', String(h));
  setHref(
    refs.spec,
    specularMap(w, h, radius, bezel, (s.lightAngle * Math.PI) / 180, s.highlightFocus, s.highlight, s.bipolar),
  );

  const rgb = hexToRgb(s.tint);
  el.style.borderRadius = `${radius}px`;
  el.style.background = `rgba(${rgb.r},${rgb.g},${rgb.b},${s.tintOpacity})`;
  el.style.border = '0';
  el.style.setProperty('--edge-lo', (s.edge * 0.42).toFixed(3));
  el.style.setProperty('--edge-hi', (s.edgePeak ?? s.edge).toFixed(3));
  el.style.setProperty('--edge-peak-angle', `${cornerConicAngle(w, h).toFixed(2)}deg`);
  // shadow <= 0 leaves box-shadow alone (removing any inline value this
  // element had) rather than forcing 'none' — so a plain CSS box-shadow on
  // the element (e.g. MainLayout.module.scss's .headerBar, matching the
  // Packages page's tier-card shadow) still shows through instead of being
  // clobbered by this always-inline style.
  if (s.shadow > 0) {
    el.style.boxShadow = `0 ${Math.round(8 + s.shadow * 10)}px ${Math.round(24 + s.shadow * 20)}px rgba(0, 0, 0, ${(s.shadow * 0.55).toFixed(2)}), inset 0 1px 0 rgba(255, 255, 255, ${(s.shadow * 0.25).toFixed(2)})`;
  } else {
    el.style.removeProperty('box-shadow');
  }
  const bf = `url(#${refs.filter.id}) blur(${s.blur}px) saturate(${s.saturate}%)`;
  el.style.backdropFilter = bf;
  el.style.setProperty('-webkit-backdrop-filter', bf);
}

const supportsGlassFilter =
  typeof CSS !== 'undefined' &&
  !!CSS.supports &&
  (CSS.supports('backdrop-filter', 'url(#a)') || CSS.supports('-webkit-backdrop-filter', 'url(#a)'));

/**
 * Applies the liquid-glass material to the returned ref's element — measures
 * it, generates its displacement/specular maps, and keeps them in sync via
 * ResizeObserver. Falls back to no-op (plain element, whatever CSS it already
 * has) on browsers without SVG-filter `backdrop-filter` support.
 *
 * Uses a callback ref (state, not useRef) so the effect re-runs whenever the
 * underlying node actually mounts/unmounts — required for a conditionally-
 * rendered target (e.g. UserMenu's dropdown, only in the DOM while open): a
 * plain useRef effect only ever fires once, at this hook's own first render,
 * which for such a target is before the node exists — so it would never
 * initialize at all.
 */
export function useLiquidGlass<T extends HTMLElement>(settings: LiquidGlassSettings) {
  const [el, setEl] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setEl(node), []);
  const rawId = useId();
  const filterId = `liquid-glass-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    if (!el || !supportsGlassFilter) return;

    const refs = makeFilter(filterId);
    let raf: number | null = null;
    const build = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        applyGlass(el, refs, settings);
      });
    };

    build();
    const ro = new ResizeObserver(build);
    ro.observe(el);
    window.addEventListener('resize', build);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', build);
      refs.filter.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `settings` is a stable literal at each call site, not state.
  }, [el, filterId]);

  return ref;
}
